import { Cloud, Keyboard, LogOut, Monitor, Moon, Search, Sun } from 'lucide-react';
import { Link } from 'react-router';
import type { Permission } from '../api/types';
import { useAuth } from '../auth/context';
import { strings } from '../strings';
import { ActiveRunsPill } from './ActiveRunsPill';
import { Breadcrumbs } from './Breadcrumbs';
import { Keys } from './Keys';
import { MenuButton, type MenuEntry } from './Menu';
import { useShell } from './shellContext';
import { ctrlKey } from './shortcuts';
import { THEME_PREFERENCES, useTheme, type ThemePreference } from './theme';

const s = strings.shell;

const THEME_ICON = { system: Monitor, light: Sun, dark: Moon } as const;

function themeEntries(preference: ThemePreference, set: (p: ThemePreference) => void): MenuEntry[] {
  return THEME_PREFERENCES.map((choice) => {
    const Icon = THEME_ICON[choice];
    return {
      kind: 'radio',
      id: `theme-${choice}`,
      label: s.theme[choice],
      icon: <Icon aria-hidden className="size-4" />,
      checked: preference === choice,
      onSelect: () => set(choice),
    };
  });
}

function ThemeMenu() {
  const { preference, setPreference } = useTheme();
  const Icon = THEME_ICON[preference];
  const label = s.theme.button(s.theme[preference]);
  return (
    <MenuButton
      buttonClassName="btn btn-ghost btn-icon"
      buttonLabel={label}
      buttonTitle={label}
      buttonContent={<Icon aria-hidden className="size-4" />}
      menuLabel={s.theme.menu}
      entries={themeEntries(preference, setPreference)}
      placement="below-end"
    />
  );
}

function UserMenu() {
  const { me, signOut } = useAuth();
  const { preference, setPreference } = useTheme();
  const entries: MenuEntry[] = [...themeEntries(preference, setPreference)];
  if (signOut) {
    entries.push(
      { kind: 'separator', id: 'sep' },
      {
        kind: 'action',
        id: 'sign-out',
        label: s.user.signOut,
        icon: <LogOut aria-hidden className="size-4" />,
        onSelect: signOut,
      },
    );
  }
  const permissions = me.permissions.filter((p): p is Permission => p in s.user.permissions);
  return (
    <MenuButton
      buttonClassName="rounded-full"
      buttonLabel={s.user.button(me.name)}
      buttonTitle={me.name}
      buttonContent={
        <span aria-hidden className="avatar">
          {s.initials(me.name)}
        </span>
      }
      menuLabel={s.user.button(me.name)}
      placement="below-end"
      header={
        <div className="max-w-[300px] px-2 pt-1.5 pb-2 text-[12px]">
          <p className="truncate text-[13px] font-semibold">{me.name}</p>
          <p className="text-fg-muted">
            {me.roles.length > 0 ? s.user.roles(me.roles.join(', ')) : s.user.noRoles}
          </p>
          {permissions.length > 0 && (
            <>
              <p className="mt-2 font-semibold">{s.user.canHeading}</p>
              <ul className="list-disc pl-4 text-fg-muted">
                {permissions.map((permission) => (
                  <li key={permission}>{s.user.permissions[permission]}</li>
                ))}
              </ul>
            </>
          )}
          <div className="menu-separator mx-0 mt-2 mb-0" />
        </div>
      }
      entries={entries}
    />
  );
}

/** The top bar (spec 6.1.7): home, breadcrumb, search, active runs, shortcuts, theme and user. */
export function TopBar() {
  const { openPalette, openShortcuts } = useShell();
  return (
    <header data-region="topbar" tabIndex={-1} className="topbar">
      <Link to="/blueprints" className="flex h-full shrink-0 items-center pr-2" aria-label={s.home}>
        <span className="flex w-[72px] justify-center text-accent">
          <Cloud aria-hidden className="size-6" />
        </span>
        <span aria-hidden className="-ml-1.5 hidden text-[15px] font-bold sm:inline">
          {strings.product}
        </span>
      </Link>
      <Breadcrumbs />
      <button
        type="button"
        className="searchbox ml-auto shrink-0 md:w-[300px] lg:w-[340px]"
        aria-label={s.search}
        aria-keyshortcuts="Control+K Meta+K"
        onClick={openPalette}
      >
        <Search aria-hidden className="size-3.5 shrink-0" />
        <span aria-hidden className="hidden truncate md:inline">
          {s.search}
        </span>
        <span aria-hidden className="ml-auto hidden md:inline">
          <Keys keys={[ctrlKey(), 'K']} />
        </span>
      </button>
      <div className="flex shrink-0 items-center gap-1">
        <ActiveRunsPill />
        <button
          type="button"
          className="btn btn-ghost btn-icon"
          aria-label={s.shortcutsButton}
          title={s.shortcutsButton}
          aria-keyshortcuts="Shift+?"
          onClick={openShortcuts}
        >
          <Keyboard aria-hidden className="size-4" />
        </button>
        <ThemeMenu />
        <UserMenu />
      </div>
    </header>
  );
}
